import React, { createContext, useState, useEffect } from 'react';
import Client from 'shopify-buy';
import { useRecoilState } from 'recoil';
import { pushAddToCartToDataLayer } from '../dataLayer/addToCart';
import { alertState } from '../recoil/snackbar';
import NoozApi from '../../services/NoozApi';
import getDefaultSearchFromUrl from '../globals/getDefaultSearchFromUrl';

export const CheckoutContext = createContext({ client: {}, checkout: {} });

export default ({ children }) => {
  const loading = useState(true);
  const checkout = useState({});
  const [alert, setAlert] = useRecoilState(alertState);

  const client = Client.buildClient({
    domain: process.env.GATSBY_SHOPIFY_URL,
    storefrontAccessToken: process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
  });

  const setLocalStorageCheckout = (newCheckout) => {
    // Set checkout id in local storage
    window.localStorage.setItem(
      `checkoutId_${process.env.GATSBY_SHOPIFY_SHOP_NAME}`,
      newCheckout.id
    );
    // Set checkout time stamp in local storage
    window.localStorage.setItem(
      `checkoutId_${process.env.GATSBY_SHOPIFY_SHOP_NAME}_timestamp`,
      Date.now() / 1000
    );
  };

  const initializeCheckoutDiscount = (check) => {
    // Apply default promo code from checkout
    if (typeof window !== 'undefined') {
      const localStoragePromocode =
        window.localStorage[
          `checkoutPromocode_${process.env.GATSBY_SHOPIFY_SHOP_NAME}`
        ];

      const promocode = getDefaultSearchFromUrl('promocode')?.toLowerCase();

      if (promocode && localStoragePromocode !== promocode) {
        try {
          client.checkout
            .addDiscount(check.id, promocode)
            .then((newCheckout) => {
              // Check the added discount exists in the new checkout
              const discountApplication = newCheckout.discountApplications.find(
                (discount) => {
                  return discount.code?.toLowerCase() === promocode;
                }
              );

              if (discountApplication) {
                window.localStorage.setItem(
                  `checkoutPromocode_${process.env.GATSBY_SHOPIFY_SHOP_NAME}`,
                  promocode
                );

                checkout[1]({ ...newCheckout });

                setAlert({
                  ...alert,
                  show: true,
                  type: 'success',
                  title: promocode.toUpperCase(),
                });
              }
            });
        } catch (err) {
          setAlert({
            ...alert,
            show: true,
            type: 'error',
            title: 'error',
          });
        }
      }
    }
  };

  const updateCheckout = async (newCheckout) => {
    initializeCheckoutDiscount(newCheckout);

    if (newCheckout && newCheckout.lineItems && newCheckout.lineItems.length) {
      let lineItems = [...newCheckout.lineItems];
      const titles = lineItems.map((item) => item.title);

      try {
        // Fetch all cart products from the Contentful API
        await NoozApi.post('/products/titles', {
          titles,
        })
          .then((response) => {
            if (response.data) {
              const reponseLineItems = response.data;
              // Merge Cart lineItems with Contentful data
              reponseLineItems.forEach((item) => {
                lineItems = lineItems.map((lineItem) => {
                  if (lineItem.title === item.shopifyKey) {
                    // Merge shopify and contentful data.
                    return { ...lineItem, ...item };
                  }
                  return lineItem;
                });
              });
            }
          })
          .catch((err) => console.log(err));

        loading[1](false);
        checkout[1]({ ...newCheckout, lineItems });
      } catch (err) {
        setAlert({
          ...alert,
          show: true,
          type: 'error',
          title: 'error',
        });
      }
    } else {
      loading[1](false);
      checkout[1]({ ...newCheckout });
    }
  };

  const createNewCheckout = () => {
    client.checkout.create().then((createdCheckout) => {
      if (createdCheckout) {
        setLocalStorageCheckout(createdCheckout);
        // Update checkout with new checkout
        updateCheckout(createdCheckout);
      }
    });
  };

  const handleLineItemRemove = (items) => {
    if (typeof window !== 'undefined') {
      const lineItemToRemove = [];

      // Initialise loading
      loading[1](true);

      items.forEach((item) => {
        const graphqlApiId = window.btoa(item.id);

        lineItemToRemove.push({
          id: graphqlApiId,
          quantity: 0,
        });
      });

      client.checkout
        .updateLineItems(checkout[0].id, lineItemToRemove)
        .then((check) => {
          // Update checkout with updated checkout
          updateCheckout(check);
        });
    }
  };

  const handleLineItemQuantityChange = (itemToChange, quantity) => {
    if (typeof window !== 'undefined') {
      const graphqlApiId = window.btoa(itemToChange.id);
      const updatedLineItem = [
        {
          id: graphqlApiId,
          quantity: parseInt(quantity, 10),
        },
      ];

      client.checkout
        .updateLineItems(checkout[0].id, updatedLineItem)
        .then((check) => {
          // Handle datalayer product add/removal
          if (quantity > 0) {
            pushAddToCartToDataLayer(itemToChange);
          } else {
            pushAddToCartToDataLayer(itemToChange);
          }
          // Update checkout with updated checkout
          updateCheckout(check);
        });
    }
  };

  const handleLineItemAdd = (itemToAdd) => {
    if (typeof window !== 'undefined') {
      const graphqlApiId = window.btoa(itemToAdd.id);
      const lineItemsToAdd = [
        {
          variantId: graphqlApiId,
          quantity: 1,
        },
      ];

      // Initialise loading
      loading[1](true);

      client.checkout
        .addLineItems(checkout[0].id, lineItemsToAdd)
        .then((check) => {
          // Handle datalayer product add
          pushAddToCartToDataLayer(itemToAdd);

          // Update checkout with updated checkout
          updateCheckout(check);
        });
    }
  };

  const handleRemoveDiscounts = () => {
    if (typeof window !== 'undefined') {
      // Initialise loading
      loading[1](true);

      client.checkout.removeDiscount(checkout[0].id).then((check) => {
        // Update checkout with updated checkout
        updateCheckout(check).then(() => {
          window.localStorage.removeItem(
            `checkoutPromocode_${process.env.GATSBY_SHOPIFY_SHOP_NAME}`
          );
        });
      });
    }
  };

  // Update and Fetch Shopify Checkout on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Has a checkout id stored in localStorage
      // Fetch checkout
      if (
        window.localStorage[
          `checkoutId_${process.env.GATSBY_SHOPIFY_SHOP_NAME}`
        ]
      ) {
        const checkoutId =
          window.localStorage[
            `checkoutId_${process.env.GATSBY_SHOPIFY_SHOP_NAME}`
          ];

        client.checkout.fetch(checkoutId).then((currentCheckout) => {
          // Checkout is valid and isn't complete
          if (currentCheckout && !currentCheckout.orderStatusUrl) {
            updateCheckout(currentCheckout);
          } else {
            // create a new checkout
            createNewCheckout();
          }
        });
      } else {
        // create a new checkout
        createNewCheckout();
      }
    }
  }, []);

  return (
    <CheckoutContext.Provider
      value={{
        loading,
        client,
        checkout,
        updateCheckout,
        handleLineItemRemove,
        handleLineItemAdd,
        handleLineItemQuantityChange,
        handleRemoveDiscounts,
      }}>
      {children}
    </CheckoutContext.Provider>
  );
};
